import type { ProviderFilter } from '@/shared/store/create-appointment-handoff';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';

/** Same pattern practitioner-react uses to find dietitians by speciality name. */
const DISCIPLINE_PATTERNS: Record<ProviderFilter['discipline'], RegExp> = {
  dietetics: /diet(itian|ician|etics?)|nutrition(ist)?/i,
};

/** True when the provider's speciality name belongs to the discipline. */
export function matchesDiscipline(
  provider: { speciality?: unknown },
  discipline: ProviderFilter['discipline'],
): boolean {
  const name = specialityDisplay(provider.speciality);
  return name ? DISCIPLINE_PATTERNS[discipline].test(name) : false;
}

/** Lower-cased speciality id of a speciality object, when it carries one. */
function skillId(speciality: unknown): string | null {
  const id = (speciality as { specialitySkillId?: unknown } | null | undefined)?.specialitySkillId;
  return typeof id === 'string' && id.trim() ? id.trim().toLowerCase() : null;
}

/**
 * Lower-cased speciality ids whose name belongs to the discipline, taken from each practitioner's main speciality and
 * every other one. ProviderSlots names only the main speciality, so the org practitioner list gives the names and the
 * ids are matched against the provider's own speciality ids (`providerSpecialityIds`) - no practitioner id is joined (PROD-1395).
 */
export function disciplineSpecialityIds(
  doctors: ReadonlyArray<{ speciality?: unknown; doctorSpecialities?: unknown[] | null }> | undefined,
  discipline: ProviderFilter['discipline'],
): Set<string> {
  const ids = new Set<string>();
  for (const d of doctors ?? []) {
    for (const s of [d.speciality, ...(d.doctorSpecialities ?? [])]) {
      const name = specialityDisplay(s);
      const id = skillId(s);
      if (id && name && DISCIPLINE_PATTERNS[discipline].test(name)) ids.add(id);
    }
  }
  return ids;
}

/**
 * Every speciality id a ProviderSlots provider carries, lower-cased: `doctorSpecialitiesIds` (a comma-separated string on
 * the wire, an array in theory) plus the main speciality's own id.
 */
export function providerSpecialityIds(provider: { speciality?: unknown; doctorSpecialitiesIds?: unknown }): string[] {
  const raw = provider.doctorSpecialitiesIds;
  const parts = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
  const ids = parts.filter((v): v is string => typeof v === 'string').map((v) => v.trim().toLowerCase()).filter(Boolean);
  const main = skillId(provider.speciality);
  if (main) ids.push(main);
  return ids;
}

/**
 * No filter: the same array comes back, so callers behave as before.
 * `extraSpecialityIds` (see `disciplineSpecialityIds`) also keeps providers whose main speciality is something else.
 */
export function filterProvidersByDiscipline<T extends { speciality?: unknown; doctorSpecialitiesIds?: unknown }>(
  providers: T[],
  filter: ProviderFilter | undefined,
  extraSpecialityIds?: ReadonlySet<string>,
): T[] {
  if (!filter) return providers;
  return providers.filter(
    (p) => matchesDiscipline(p, filter.discipline) || (!!extraSpecialityIds?.size && providerSpecialityIds(p).some((id) => extraSpecialityIds.has(id))),
  );
}

/** Server page size of the practitioner list; a list this long may be cut. */
export const PRACTITIONER_LIST_PAGE_SIZE = 1000;

/** Warn once per list length: a cut list can miss second specialities (the wrapper's totals are 0 on dev, so count rows). */
export function warnIfPractitionerListMayBeCut(rowCount: number): void {
  if (rowCount < PRACTITIONER_LIST_PAGE_SIZE) return;
  console.warn(
    `[referral-filter] practitioner list has ${rowCount} rows; it may be cut at the server page size (${PRACTITIONER_LIST_PAGE_SIZE}) and some second specialities may be missed.`,
  );
}

/** One line for the provider step when the filter leaves nobody. */
export function noProviderForFilterText(filter: ProviderFilter): string {
  return `No ${filter.label.toLowerCase()} is set up for this practice. Add one in Settings, or book without the referral.`;
}
